import { useState } from "react";
import { PickupPlayer } from "./PickupPlayer";
import { Icon } from "./icons";
import { CATEGORY_LABEL, categoryOf, type FlagCategory } from "../lib/reportStore";
import { BASE_WORDS, STEP_WORDS, buildContext, type CtxToken } from "../lib/pickupContext";

/**
 * Normalised input for one pickup card, source-agnostic so the owner Package and
 * the public share page render identically. Supply `tokens` + `flagFirst`/
 * `flagLast` for live windowed context (owner: full manuscript + global indices;
 * share: a bounded slice + local indices); otherwise the snapshot fallback
 * (`context_before/after` + `fallbackFlag`) is shown.
 */
export interface PickupCardInput {
  flag_key: string;
  addressed: boolean;
  audio_filename?: string | null;
  page?: number | null;
  flag_type: string;
  category?: string; // reviewer's display-category override ("" / undefined ⇒ derive from flag_type)
  title?: string; // one-line headline (edited title or generated), shown at a glance
  comment?: string;
  timecode?: string;
  audio_url: string | null;
  tokens?: CtxToken[] | null;
  flagFirst?: number;
  flagLast?: number;
  heardText?: string;
  // snapshot fallback
  context_before?: string;
  context_after?: string;
  fallbackFlag?: string;
  start_time?: number | null;
  end_time?: number | null;
  // owner-only: the chapter result is still loading, so commentary isn't ready
  contextLoading?: boolean;
}

// The bold "what" that leads the demoted note, per flag category.
const WHAT: Record<string, string> = {
  misread: "Misread",
  missing: "Missing word",
  added: "Added word",
  pause: "Long pause",
  noise: "Noise",
  defect: "Defect",
  manual: "Manual flag",
};

// Trim the redundant trailing "#misread"-style tag the report appends.
function cleanNote(s: string): string {
  return s.replace(/\s*#\w+\s*$/, "").trim();
}

export function PickupCard({
  input,
  index,
  total,
  onAddressed,
}: {
  input: PickupCardInput;
  index: number;
  total?: number;
  onAddressed: (addressed: boolean) => void;
}) {
  const [extra, setExtra] = useState(0); // extra words each side beyond BASE
  const [copied, setCopied] = useState(false);
  const wordsEachSide = BASE_WORDS + extra;

  const cat: FlagCategory = input.category ? (input.category as FlagCategory) : categoryOf(input.flag_type || "");
  const isPause = cat === "pause";
  // An "added" flag's quote IS the narrator's inserted word — it's NOT in the
  // manuscript. The before/after around it are the real manuscript, so render the
  // inserted word as an insertion (a leading ＋) so it doesn't read as the script.
  const isInsertion = (input.flag_type || "").toLowerCase() === "added";

  const hasTokens = !!input.tokens && input.flagFirst != null && input.flagLast != null;
  const ctx = hasTokens
    ? buildContext(input.tokens!, input.flagFirst!, input.flagLast!, input.heardText ?? "", wordsEachSide)
    : null;

  const before = ctx ? ctx.before : input.context_before ?? "";
  const flag = ctx ? ctx.flag : input.fallbackFlag || "";
  const after = ctx ? ctx.after : input.context_after ?? "";
  const audioStart = ctx ? ctx.audioStart : input.start_time ?? null;
  const audioEnd = ctx ? ctx.audioEnd : input.end_time ?? null;
  const hasMore = ctx?.hasMore ?? false;

  const note = cleanNote(input.comment ?? "");
  // The full readable explanation, copied to the clipboard from the note.
  const explanation = `${WHAT[cat] ?? "Flag"}${note ? ` — ${note}` : ""}`;
  async function copyExplanation() {
    try {
      await navigator.clipboard.writeText(explanation);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked */
    }
  }

  const dur =
    input.end_time != null && input.start_time != null && input.end_time > input.start_time
      ? input.end_time - input.start_time
      : null;
  const pauseLabel = dur ? `${dur.toFixed(1)}s silence` : flag || "silence";

  const flagNode = isPause ? (
    <span className="pkc-pause">
      <Icon name="pause" size={12} /> {pauseLabel}
    </span>
  ) : isInsertion ? (
    <span className="pkc-hl pkc-ins" title="Added by the narrator — not in the manuscript">{flag || "—"}</span>
  ) : (
    <span className="pkc-hl">{flag || "—"}</span>
  );

  return (
    <div className={`pickup-card fc-${cat}${input.addressed ? " addressed" : ""}`}>
      <div className="pkc-head">
        <span className="pkc-type">
          <span className="pkc-dot" />
          {CATEGORY_LABEL[cat]}
        </span>
        {input.audio_filename && (
          <>
            <span className="pkc-sep">·</span>
            <span className="pkc-fn">{input.audio_filename}</span>
          </>
        )}
        {input.page != null && (
          <>
            <span className="pkc-sep">·</span>
            <span className="pkc-fn">p.{input.page}</span>
          </>
        )}
        {input.timecode && (
          <>
            <span className="pkc-sep">·</span>
            <span className="pkc-fn">{input.timecode}</span>
          </>
        )}
        <span className="pkc-idx">{total ? `${index + 1} / ${total}` : index + 1}</span>
      </div>

      {/* For a manual flag the highlighted "quote" below IS the title, so don't
          repeat it as a separate headline. */}
      {input.title && cat !== "manual" && <div className="pkc-title">{input.title}</div>}

      <div className="pkc-quote">
        {/* `ctx` carries the manuscript's original separators (punctuation), so
            no manual spacing; the snapshot fallback joins with single spaces. */}
        {before && <>{before}{ctx ? "" : " "}</>}
        {flagNode}
        {after && <>{ctx ? "" : " "}{after}</>}
        {(hasMore || extra > 0) && (
          <span className="pkc-morewrap">
            {hasMore && (
              <button className="pkc-more" onClick={() => setExtra((e) => e + STEP_WORDS)}>… more</button>
            )}
            {hasMore && extra > 0 && <span className="pkc-moresep">·</span>}
            {extra > 0 && (
              <button className="pkc-less" onClick={() => setExtra(0)}>less</button>
            )}
          </span>
        )}
      </div>

      <div className="pkc-note">
        <span className="pkc-nd" />
        <span className="pkc-nt">
          <b>{WHAT[cat] ?? "Flag"}</b>
          {note ? <> — {note}</> : input.contextLoading ? <> — loading commentary…</> : null}
        </span>
        {note && (
          <button
            className={`pkc-copy${copied ? " copied" : ""}`}
            onClick={copyExplanation}
            title="Copy explanation"
            aria-label={copied ? "Explanation copied" : "Copy explanation"}
          >
            <Icon name={copied ? "check" : "copy"} size={14} />
          </button>
        )}
      </div>

      <div className="pkc-foot">
        <PickupPlayer src={input.audio_url} start={audioStart} end={audioEnd} />
        <label className={`pkc-addr${input.addressed ? " done" : ""}`}>
          <input
            type="checkbox"
            checked={input.addressed}
            onChange={(e) => onAddressed(e.target.checked)}
          />
          <span className="pkc-box"><Icon name="check" size={13} /></span>
          Addressed
        </label>
      </div>
    </div>
  );
}
